import { useRealtimeNotificationStore } from '../../store/realtimeNotificationStore';
import { unlockSpeechSynthesis } from '../../utils/speakNotification';
import { BellFill, ChatLeftTextFill } from 'react-bootstrap-icons';
import { ReactComponent as DownIcon } from '../../images/chevron-down.svg';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Navbar, Badge } from 'react-bootstrap';
import NotificationDropdown from './NotificationDropdown';
import ProfileMenuDropdown, { ProfileMenuItemKey } from './ProfileMenuDropdown';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../images/SKIP.svg';
import HeaderCalendarButton from './HeaderCalendarButton';
// import { useIsMobile } from './useIsMobile';
import { toast } from 'react-toastify';
import {
  deactivateUserAccount,
  deleteNotification,
  deleteUserAccount,
  type AppNotification,
  markNotificationAsRead,
} from '../Preview/api';
import axiosInstance, { logoutUser } from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { shouldShowPostOnboardingNav } from '../../utils/navVisibility';
import { resolveProfileImageUrl } from '../../utils/userProfile';
import {
  resolveNotificationAction,
  resolveNotificationPath,
} from '../../utils/notificationPageUtils';
import {
  canRespondToProfileAccessNotification,
  parseProfileAccessParams,
  resolveNotificationNavigationTarget,
} from '../../utils/notificationActions';
// import { io } from 'socket.io-client';

interface DesktopHeaderProps {
  onOpenNotifications: () => void;
  onOpenSettings: () => void;
  unreadCount: number;
  userData: any;
  fullWidthHeader?: boolean;
  showOnboardingLogout?: boolean;
}
// interface HeaderProps {
//   onToggleSidebar: () => void;
// }

type Notification = AppNotification & {
  conversationId?: string;
  recruiterEmail?: string;
  tag?: string;
  direction?: string;
};

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  onOpenNotifications,
  onOpenSettings: _onOpenSettings,
  userData,
  fullWidthHeader = false,
  showOnboardingLogout = false,
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const userNotificationData = useRealtimeNotificationStore((s) => s.items) as Notification[];
  const markItemReadInStore = useRealtimeNotificationStore((s) => s.markItemRead);
  const removeItemFromStore = useRealtimeNotificationStore((s) => s.removeItem);

  const bellRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [showNotificationDetail, setShowNotificationDetail] = useState(false);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [latestMessageConversationId] = useState<string | null>(null);

  const [deleteMode, setDeleteMode] = useState<'deactivate' | 'delete'>('delete');
  const [confirmText, setConfirmText] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const [showAll, setShowAll] = useState(false);
  const [profileImageBroken, setProfileImageBroken] = useState(false);

  const profileImageUrl = resolveProfileImageUrl(userData?.profileImg);
  const showProfilePhoto = Boolean(profileImageUrl) && !profileImageBroken;

  const showNavChrome = shouldShowPostOnboardingNav(location.pathname, userData);

  useEffect(() => {
    const unlockSpeech = () => {
      unlockSpeechSynthesis();
      document.removeEventListener('click', unlockSpeech);
      document.removeEventListener('keydown', unlockSpeech);
    };

    document.addEventListener('click', unlockSpeech);
    document.addEventListener('keydown', unlockSpeech);

    return () => {
      document.removeEventListener('click', unlockSpeech);
      document.removeEventListener('keydown', unlockSpeech);
    };
  }, []);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setDropdownOpen(false);
      }
    }
    if (isDropdownOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (userData?.profileImg) {
      setProfileImageBroken(false);
    }
  }, [userData?.profileImg]);

  // Close overlay when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        !bellRef.current?.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    }

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const accessToken = localStorage.getItem('accessToken');

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
    setShowNotifications(false);
  };

  const profileDisplayName =
    userData?.username || [userData?.fname, userData?.lname].filter(Boolean).join(' ') || undefined;

  const handleLogout = () => {
    logoutUser();
  };

  const handleProfileMenuSelect = (key: ProfileMenuItemKey) => {
    setDropdownOpen(false);
    switch (key) {
      case 'settings':
        navigate('/user-settings');
        break;
      case 'profile':
        navigate('/user-profile');
        break;
      case 'connections':
        navigate('/account/connections');
        break;
      case 'security':
        navigate('/settings/security');
        break;
      case 'privacy':
        navigate('/settings/privacy');
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      case 'logout':
        void handleLogout();
        break;
      default:
        toast.info('Coming soon');
    }
  };

  const handleOpenMessages = () => {
    if (latestMessageConversationId) {
      navigate(`/messages?conversationId=${encodeURIComponent(latestMessageConversationId)}`);
    } else {
      navigate('/messages');
    }

    setMessageUnreadCount(0);
  };

  useEffect(() => {
    if (location.pathname.startsWith('/messages')) {
      setMessageUnreadCount(0);
    }
  }, [location.pathname]);

  const unreadCount = userNotificationData.filter((n) => !n.isRead).length;

  const parseLikedPromptMessage = (message: string) => {
    const result = {
      recruiterName: 'A recruiter',
      promptText: '',
    };
    const promptMatch = message.match(/^(.*?)\s+liked your prompt:\s*"([^"]+)"\.?$/i);
    if (promptMatch) {
      result.recruiterName = promptMatch[1]?.trim() || result.recruiterName;
      result.promptText = promptMatch[2]?.trim() || '';
      return result;
    }
    const recruiterMatch = message.match(/^(.*?)\s+liked your prompt/i);
    if (recruiterMatch?.[1]) {
      result.recruiterName = recruiterMatch[1].trim();
    }
    return result;
  };

  const markSingleNotificationAsRead = async (notif: Notification) => {
    try {
      await markNotificationAsRead(String(notif.id), {
        message: notif.message,
      });
      markItemReadInStore(String(notif.id));
      setSelectedNotification((prev) =>
        prev && prev.id === notif.id ? { ...prev, isRead: true } : prev
      );
    } catch (err) {
      console.error('Failed to mark notification as read', err);
      toast.error('Could not mark this notification as read.');
    }
  };

  const openNotification = async (notif: Notification) => {
    setShowNotifications(false);
    if (!notif.isRead) {
      await markSingleNotificationAsRead(notif);
    }
    navigate(resolveNotificationNavigationTarget(notif));
  };

  const selectedLikePromptDetails = selectedNotification
    ? parseLikedPromptMessage(selectedNotification.message || '')
    : null;
  const isSelectedLikedPrompt = Boolean(
    selectedNotification?.message?.toLowerCase().includes('liked your prompt')
  );

  const canRespondToProfileAccess = (notif: Notification): boolean =>
    canRespondToProfileAccessNotification(notif);

  const handleRespondToProfileAccessRequest = async (
    notif: Notification,
    action: 'accept' | 'deny'
  ) => {
    const { recruiterEmail, accessRequestId } = parseProfileAccessParams(notif);
    if (!recruiterEmail) {
      toast.error('Unable to determine recruiter email for this request.');
      return;
    }

    try {
      await axiosInstance.post(API_ROUTES.RECRUITER.RESPOND_ACCESS_REQUEST, {
        recruiterEmail,
        action,
        accessRequestId,
      });

      await markNotificationAsRead(String(notif.id), {
        message: notif.message,
      });
      markItemReadInStore(String(notif.id));

      toast.success(
        action === 'accept'
          ? 'Access approved. Recruiter has been emailed their secure profile link.'
          : 'Access request denied.'
      );
    } catch (error) {
      console.error('Failed to respond to access request:', error);
      toast.error('Could not update this access request right now.');
    }
  };

  return (
    <>
      <div className={`header-container${fullWidthHeader ? ' header-container--full-width' : ''}`}>
        <Navbar className="mobile-header" variant="light">
          {/* New: Mobile Nav Icon */}
          {/** <div className="mobile-nav-icon" onClick={onToggleSidebar}>
            ☰ {/* Or use an SVG if you prefer 
          </div>*/}
          <div className="header-brand-cluster d-flex align-items-center gap-2 gap-md-3 flex-wrap">
            <Navbar.Brand as={Link} to="/dashboard" className="logo mb-0">
              <img alt="SkipScreen" src={Logo} className="site-logo" width={120} />
            </Navbar.Brand>
          </div>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        </Navbar>
        {accessToken && showOnboardingLogout && !showNavChrome && (
          <button type="button" className="header-onboarding-logout" onClick={handleLogout}>
            Log out
          </button>
        )}
        {accessToken && showNavChrome && (
          <nav style={{ position: 'relative' }} ref={profileMenuRef}>
            <div className="header-user-data-wrapper">
              {/* <span style={{ margin: 'auto' }}>
                  <span className="down-arrow">
                    <BellIcon style={{ width: '0px', height: '20px' }} />
                  </span>
                </span> */}
              {showNavChrome && <HeaderCalendarButton />}
              {showNavChrome && (
                <div className="notif-dropdown-anchor" ref={bellRef}>
                  <button
                    type="button"
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="position-relative header-icon-btn"
                    aria-expanded={showNotifications}
                    aria-haspopup="dialog"
                    aria-label="Notifications"
                  >
                    <BellFill size={20} aria-hidden />
                    {unreadCount > 0 && (
                      <Badge bg="danger" pill className="header-notif-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </button>
                  <NotificationDropdown
                    show={showNotifications}
                    notifications={userNotificationData}
                    showAll={showAll}
                    onToggleShowAll={() => setShowAll(!showAll)}
                    onClose={() => setShowNotifications(false)}
                    onOpen={(n) => void openNotification(n)}
                    onMarkRead={(n) => void markSingleNotificationAsRead(n)}
                    onDelete={async (n) => {
                      try {
                        await deleteNotification(n.id);
                        removeItemFromStore(String(n.id));
                      } catch (err) {
                        console.error('Failed to delete notification', err);
                      }
                    }}
                    canRespondToProfileAccess={canRespondToProfileAccess}
                    onRespondProfileAccess={(n, action) =>
                      void handleRespondToProfileAccessRequest(n, action)
                    }
                  />
                </div>
              )}
              {showNavChrome && (
                <Button
                  variant="light"
                  onClick={handleOpenMessages}
                  className="position-relative"
                  aria-label="Open messages"
                >
                  <ChatLeftTextFill size={20} />
                  {messageUnreadCount > 0 && (
                    <Badge
                      bg="danger"
                      pill
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: '0.6rem' }}
                    >
                      {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
                    </Badge>
                  )}
                </Button>
              )}
              <div className="profile-menu-anchor">
                {showProfilePhoto ? (
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="flex items-center gap-1 profile-menu-trigger"
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="dialog"
                  >
                    <img
                      src={profileImageUrl!}
                      alt="Profile Avatar"
                      className="preview-avatar-wrapper"
                      style={{ width: '40px', height: '40px' }}
                      onError={() => setProfileImageBroken(true)}
                    />
                    <span className="down-arrow flex items-center justify-center leading-none">
                      <DownIcon style={{ width: '20px', height: '20px' }} />
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    title="Open account menu"
                    className="header-avatar profile-menu-trigger"
                    onClick={toggleDropdown}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="dialog"
                  />
                )}
                <ProfileMenuDropdown
                  show={isDropdownOpen}
                  displayName={profileDisplayName}
                  onClose={() => setDropdownOpen(false)}
                  onSelect={handleProfileMenuSelect}
                />
              </div>
            </div>
          </nav>
        )}
      </div>
      <Modal
        show={showNotificationDetail && Boolean(selectedNotification)}
        onHide={() => {
          setShowNotificationDetail(false);
          setSelectedNotification(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Notification Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNotification ? (
            <>
              <div className="notification-detail-alert">
                {isSelectedLikedPrompt ? (
                  <>
                    <strong>{selectedLikePromptDetails?.recruiterName || 'A recruiter'}</strong>{' '}
                    liked one of your prompts.
                  </>
                ) : (
                  selectedNotification.message
                )}
              </div>

              {isSelectedLikedPrompt && selectedLikePromptDetails?.promptText ? (
                <div className="notification-detail-section">
                  <div className="notification-detail-label">Prompt</div>
                  <div className="notification-detail-value">
                    "{selectedLikePromptDetails.promptText}"
                  </div>
                </div>
              ) : null}

              <div className="notification-detail-meta">
                {selectedNotification.createdAt
                  ? new Date(selectedNotification.createdAt).toLocaleString()
                  : new Date().toLocaleString()}
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="notification-detail-footer">
          {selectedNotification && !selectedNotification.isRead ? (
            <Button
              variant="outline-success"
              onClick={async () => {
                await markSingleNotificationAsRead(selectedNotification);
              }}
            >
              Mark as read
            </Button>
          ) : null}
          {selectedNotification ? (
            <Button
              variant="success"
              onClick={() => {
                const action = resolveNotificationAction(selectedNotification);
                setShowNotificationDetail(false);
                setSelectedNotification(null);
                if (action) {
                  navigate(action.href || resolveNotificationPath(selectedNotification));
                }
              }}
            >
              {resolveNotificationAction(selectedNotification)?.label || 'Open details'}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {deleteMode === 'delete' ? 'Delete account' : 'Deactivate account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="delete-mode-delete"
              name="delete-mode"
              label="Delete permanently"
              checked={deleteMode === 'delete'}
              onChange={() => setDeleteMode('delete')}
            />
            <div className="text-muted small ms-4 mb-2">
              Irreversible. Your profile, uploads, and personal data will be removed. Some records
              (e.g., invoices) may be retained where legally required or anonymised.
            </div>
            <Form.Check
              type="radio"
              id="delete-mode-deactivate"
              name="delete-mode"
              label="Deactivate (can be reactivated later)"
              checked={deleteMode === 'deactivate'}
              onChange={() => setDeleteMode('deactivate')}
            />
            <div className="text-muted small ms-4">
              Your profile becomes invisible and logins are blocked. Data is retained.
            </div>
          </div>
          {deleteMode === 'delete' && (
            <>
              <div className="border rounded p-2 bg-light mb-2 small">
                To confirm, type <strong>DELETE</strong> below.
              </div>
              <Form.Control
                type="text"
                placeholder="Type DELETE to confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          {deleteMode === 'deactivate' ? (
            <Button
              variant="warning"
              onClick={async () => {
                try {
                  setIsDeleting(true);
                  await deactivateUserAccount();
                  toast.success('Account deactivated');
                  // revoke local auth & redirect
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  navigate('/', { replace: true });
                } catch (e) {
                  const msg =
                    (e as { response?: { data?: { message?: string } } })?.response?.data
                      ?.message || 'Failed to deactivate account';
                  toast.error(msg);
                } finally {
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Working...' : 'Deactivate'}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={async () => {
                if (confirmText !== 'DELETE') {
                  toast.info('Please type DELETE to confirm.');
                  return;
                }
                try {
                  setIsDeleting(true);
                  await deleteUserAccount();
                  toast.success('Account deleted');
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  navigate('/', { replace: true });
                } catch (e) {
                  const msg =
                    (e as { response?: { data?: { message?: string } } })?.response?.data
                      ?.message || 'Failed to delete account';
                  toast.error(msg);
                } finally {
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete account'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
  // return <div className="desktop-header-container"></div>;
};

export default DesktopHeader;
